import { ReadonlyVec3, vec3 } from 'gl-matrix';

/**
 * Pure geometry in the patient frame, in mm: points and the planes they are
 * tested against.
 *
 * Patient space already encodes voxel spacing and orientation, so a Euclidean
 * distance here is physically exact. Do NOT re-multiply by voxel spacing.
 */

/** A point in the patient coordinate frame, in mm. */
export type PointMm = readonly [number, number, number];

/** A gl-matrix vector as a plain mm tuple: serializable, and double precision. */
export function toPointMm(point: ReadonlyVec3): PointMm {
    return [point[0], point[1], point[2]];
}

/** Euclidean distance in mm. Zero is a valid result; the caller decides. */
export function euclideanDistanceMm(a: PointMm, b: PointMm): number {
    return vec3.distance(a, b);
}

/** Midpoint in mm, in double precision. */
export function midpointMm(a: PointMm, b: PointMm): PointMm {
    return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
}

/**
 * Interior angle at `vertex`, degrees [0, 180]. `atan2(|u × v|, u · v)` stays
 * precise for near-collinear arms where `acos` does not.
 *
 * @returns NaN for a zero-length arm, which the caller must refuse.
 */
export function angleDegAtVertex(
    armA: PointMm,
    vertex: PointMm,
    armB: PointMm
): number {
    const u = vec3.subtract(vec3.create(), armA, vertex);
    const v = vec3.subtract(vec3.create(), armB, vertex);
    if (vec3.length(u) === 0 || vec3.length(v) === 0) {
        return NaN;
    }
    const crossMagnitude = vec3.length(vec3.cross(vec3.create(), u, v));
    return (Math.atan2(crossMagnitude, vec3.dot(u, v)) * 180) / Math.PI;
}

/**
 * A plane through `point` with unit `normal`, patient space (mm). The
 * equivalent `offsetMm` form used by the helpers below is
 * `dot(point, normal)`.
 */
export interface PlaneMm {
    readonly point: ReadonlyVec3;
    readonly normal: ReadonlyVec3;
}

/**
 * The plane's signed offset along its own normal — the `offsetMm` form the
 * helpers below take. Two planes with the same normal and the same offset are
 * the same plane, however far apart their `point`s lie within it.
 */
export function planeOffsetMm(plane: PlaneMm): number {
    return (
        plane.point[0] * plane.normal[0] +
        plane.point[1] * plane.normal[1] +
        plane.point[2] * plane.normal[2]
    );
}

/** A plane is a unit `normal` plus its `offsetMm` along that normal. */
export function planeDistanceMm(
    point: PointMm,
    normal: ReadonlyVec3,
    offsetMm: number
): number {
    return (
        point[0] * normal[0] +
        point[1] * normal[1] +
        point[2] * normal[2] -
        offsetMm
    );
}

/** Whether EVERY point lies within `toleranceMm` of the plane. */
export function arePointsOnPlane(
    points: readonly PointMm[],
    normal: ReadonlyVec3,
    offsetMm: number,
    toleranceMm: number
): boolean {
    return points.every(
        (point) =>
            Math.abs(planeDistanceMm(point, normal, offsetMm)) <= toleranceMm
    );
}

/** `point` moved along `normal` onto the plane, keeping its in-plane position. */
export function projectPointOntoPlane(
    point: PointMm,
    normal: ReadonlyVec3,
    offsetMm: number
): PointMm {
    const distanceMm = planeDistanceMm(point, normal, offsetMm);
    const projected = vec3.scaleAndAdd(
        vec3.create(),
        point,
        normal,
        -distanceMm
    );
    return [projected[0], projected[1], projected[2]];
}

/** A patient axis by index: 0 is LR (X), 1 is AP (Y), 2 is SI (Z). */
type PatientAxisIndex = 0 | 1 | 2;

/** The patient axis a direction runs closest to, and which way along it. */
export interface DominantComponent {
    index: PatientAxisIndex;
    /** The sign of that component; a zero component counts as positive. */
    sign: 1 | -1;
}

/**
 * The component of `direction` with the largest magnitude, the earliest
 * axis winning a tie. A patient-aligned direction has one non-zero
 * component; an oblique one is nearest the axis this names.
 */
export function dominantComponent(direction: ReadonlyVec3): DominantComponent {
    let index: PatientAxisIndex = 0;
    for (const candidate of [1, 2] as const) {
        if (Math.abs(direction[candidate]) > Math.abs(direction[index])) {
            index = candidate;
        }
    }
    return { index, sign: direction[index] < 0 ? -1 : 1 };
}
